/**
 * Cart Notification Added - Enhanced cart notification component
 * Shows a notification when items are added to cart
 */
class CartNotificationAdded extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification-added');
    this.wrapper = document.getElementById('cart-notification-added-wrapper');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onCloseClick = this.close.bind(this);
    this.addedToCartUnsubscriber = undefined;

    if (!this.notification) return;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  connectedCallback() {
    // Set up close button listeners
    const closeButtons = this.querySelectorAll('button[type="button"].cna__close');
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', this.onCloseClick);
    });

    // Set up overlay click listener to close notification
    if (this.wrapper) {
      this.wrapper.addEventListener('click', (evt) => {
        // Only close if clicking directly on the wrapper (overlay), not on the modal
        if (evt.target === this.wrapper) {
          this.close();
        }
      });
    }

    // Subscribe to added to cart events
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.addedToCartUnsubscriber = subscribe(PUB_SUB_EVENTS.addedToCart, (event) => {
        if (event && event.cartData) {
          this.handleAddedToCart(event);
        }
      });
    } else {
      console.warn('[CartNotification] subscribe function or PUB_SUB_EVENTS not available');
    }
  }

  disconnectedCallback() {
    // Remove close button listeners
    const closeButtons = this.querySelectorAll('button[type="button"].cna__close');
    closeButtons.forEach((closeButton) => {
      closeButton.removeEventListener('click', this.onCloseClick);
    });

    if (this.addedToCartUnsubscriber) {
      this.addedToCartUnsubscriber();
    }
  }

  /**
   * Handle added to cart event
   * @param {Object} event - Added to cart event with cartData from /add.js response
   */
  handleAddedToCart(event) {
    if (!event || !event.cartData) {
      console.warn('[CartNotification] handleAddedToCart: missing event or cartData', event);
      return;
    }

    const cartData = event.cartData;
    let lastItem = null;
    
    if (cartData.items && Array.isArray(cartData.items) && cartData.items.length > 0) {
      lastItem = cartData.items[cartData.items.length - 1];
    } 

    else if (cartData.key && cartData.variant_id) {
      lastItem = cartData;
    }

    if (!lastItem) {
      console.warn('[CartNotification] handleAddedToCart: could not extract item from response', cartData);
      return;
    }

    const notificationData = {
      item: lastItem,
      key: lastItem.key,
    };

    this.renderContents(notificationData);
  }

  /**
   * Open the notification
   */
  open() {
    if (!this.notification) return;

    // Scroll to top smoothly for desktop
    if (window.innerWidth >= 750) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Remove hide class first (display: none -> display: block)
    if (this.wrapper) {
      this.wrapper.classList.remove('hide');
    }

    // Use requestAnimationFrame to ensure the browser processes the hide removal
    requestAnimationFrame(() => {
      this.notification.classList.add('animate', 'active');
      
      // On mobile, also activate the wrapper overlay
      if (this.wrapper) {
        this.wrapper.classList.add('active');
      }
    });

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        if (typeof trapFocus === 'function') {
          trapFocus(this.notification);
        }
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    document.body.classList.add('overflow-hidden');
    const aliaElement = document.querySelector('[id^="alia-root-"]');
    if (aliaElement) {
      aliaElement.style.display = 'none';
    }
    const runMicroApp = document.querySelector('.rp-micro-app-dummy-icon-container');
    if (runMicroApp) {
      runMicroApp.style.display = 'none';
    }
  }

  /**
   * Close the notification
   */
  close() {
    if (!this.notification) return;

    // Remove active class to trigger close animation
    this.notification.classList.remove('active', 'animate');
    
    if (this.wrapper) {
      this.wrapper.classList.remove('active');
      // Add hide class after transition completes
      const handleTransitionEnd = (e) => {
        // Only handle if it's the modal's transition
        if (e.target === this.notification && this.wrapper && !this.wrapper.classList.contains('active')) {
          this.wrapper.classList.add('hide');
        }
      };
      this.notification.addEventListener('transitionend', handleTransitionEnd, { once: true });
      // Fallback: add hide class after animation completes
      setTimeout(() => {
        if (this.wrapper && !this.wrapper.classList.contains('active') && !this.wrapper.classList.contains('hide')) {
          this.wrapper.classList.add('hide');
        }
        const aliaElement = document.querySelector('[id^="alia-root-"]');
        if (aliaElement) {
          aliaElement.style.display = '';
        }
        const runMicroApp = document.querySelector('.rp-micro-app-dummy-icon-container');
        if (runMicroApp) {
          runMicroApp.style.display = '';
        }
      }, 600);
    }

    document.body.removeEventListener('click', this.onBodyClick);
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function' && this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
  }

  formatMoney(cents) {
    return window.formatMoney ? window.formatMoney(cents) : `$${cents / 100}`;
  }

  /**
   * Populate product data from /add.js response
   * @param {Object} item - Cart item from /add.js response
   */
  populateProductData(item) {
    if (!item) return;

    const productElement = document.getElementById('cart-notification-added-product');
    if (!productElement) return;

    const cartItem = productElement.querySelector('.cart-item');
    if (cartItem) {
      cartItem.setAttribute('data-item-key', item.key || '');
    }

    // Product image
    const imageEl = productElement.querySelector('[data-product-image]');
    if (imageEl) {
      const mockupImageUrl = item.properties?._Mockup || item.properties?.['_Mockup'] || '';
      const imageUrl = mockupImageUrl || item.image || (item.featured_image?.url || '');
      const imageAlt = item.featured_image?.alt || item.product_title || '';
      const aspectRatio = item.featured_image?.aspect_ratio || 1.0;
      
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = imageAlt;
        imageEl.height = Math.round(100 / aspectRatio);
        imageEl.style.display = '';
      } else {
        imageEl.style.display = 'none';
      }
    }

    // Product title
    const titleEl = productElement.querySelector('[data-product-title]');
    if (titleEl) {
      titleEl.textContent = item.product_title || item.title || '';
    }

    // Variant options
    const variantEl = productElement.querySelector('[data-product-variant]');
    const variantOptionsEl = productElement.querySelector('[data-variant-options]');
    if (variantEl && variantOptionsEl) {
      const variantOptions = item.options_with_values || [];
      if (variantOptions.length > 0) {
        let variantText = '';
        variantOptions.forEach((option, index) => {
          variantText += option.value || '';
          if (index < variantOptions.length - 1) {
            variantText += ' / ';
          }
        });
        variantOptionsEl.textContent = variantText;
        variantEl.style.display = '';
      } else {
        variantEl.style.display = 'none';
      }
    }

    // Discount
    const discountEl = productElement.querySelector('[data-product-discount]');
    const discountCodeEl = productElement.querySelector('[data-discount-code]');
    if (discountEl && discountCodeEl) {
      if (item.discounts && item.discounts.length > 0) {
        let discountText = '';
        item.discounts.forEach((discount, index) => {
          const discountTitle = typeof discount === 'string' ? discount : (discount.title || discount.code || '');
          discountText += discountTitle;
          if (index < item.discounts.length - 1) {
            discountText += ', ';
          }
        });
        discountCodeEl.textContent = discountText;
        discountEl.style.display = '';
      } else {
        discountEl.style.display = 'none';
      }
    }

    // Price
    const priceCurrentEl = productElement.querySelector('[data-price-current]');
    const priceOriginalEl = productElement.querySelector('[data-price-original]');
    if (priceCurrentEl) {
      const linePrice = item.line_price || 0;
      priceCurrentEl.textContent = this.formatMoney(linePrice);
    }
    if (priceOriginalEl) {
      const originalLinePrice = item.original_line_price || item.line_price || 0;
      const linePrice = item.line_price || 0;
      const hasDiscount = originalLinePrice > linePrice;
      
      if (hasDiscount) {
        priceOriginalEl.textContent = this.formatMoney(originalLinePrice);
        priceOriginalEl.style.display = '';
      } else {
        priceOriginalEl.style.display = 'none';
      }
    }
  }


  renderContents(parsedState) {
    if (!this.notification) return;

    this.cartItemKey = parsedState.key;
    
    if (parsedState.item) {
      this.populateProductData(parsedState.item);
    }

   

    if (this.header && typeof this.header.reveal === 'function') {
      this.header.reveal();
    }
    
    this.open();
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification-added')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      if (disclosure) {
        const summary = disclosure.querySelector('summary');
        this.activeElement = summary;
      }
      this.close();
    }
  }


  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification-added', CartNotificationAdded);

/**
 * Helper function to show cart notification after adding item
 * This can be called from product form submissions
 */
window.showCartNotificationAdded = function(cartData) {
  const cartNotification = document.querySelector('cart-notification-added');
  if (cartNotification && typeof cartNotification.renderContents === 'function') {
    cartNotification.renderContents(cartData);
  }
};


